import { useMemo } from 'react'
import { useResurrectionCampaign } from 'src/screens/resurrection/hooks/useResurrectionCampaign'
import { useResurrectionFlags } from './useResurrectionFlags'

interface ResurrectionData {
  // Hero section data
  heroTitle: string
  heroSubtitle: string
  heroPromoCopy: string
  ctaLabel: string
  heroImage: string
  heroImageMobile?: string
  browserTitle: string

  // Featured meals data
  featuredMeals: Array<{
    id: string | number
    name: string
    imageUrl?: string
    image_url?: string
    description?: string
    chef?: {
      name: string
      id: string | number
    }
  }>

  // Campaign data
  campaignId?: string
  couponCode?: string
  conditionsCopy?: string
  highlightedMealsIds: number[]
  chefBrandIds: number[]

  // Loading states
  isLoading: boolean
  isError: boolean
}

interface UseResurrectionDataParams {
  campaignSlug?: string
  actions: any
  isResurrectionCampaignLoading: boolean
  // Router props (from Redux)
  resurrectionCampaign?: any
  featuredMeals?: any
  heroTitle?: string
  heroSubtitle?: string
  text?: string
  conditionsText?: string
  browserTitle?: string
  coupon?: string
  prepaidOfferId?: string
}

export function useResurrectionData({
  campaignSlug,
  actions,
  isResurrectionCampaignLoading,
  // Router props
  resurrectionCampaign,
  featuredMeals,
  heroTitle,
  heroSubtitle,
  text,
  conditionsText,
  browserTitle,
  coupon,
  prepaidOfferId
}: UseResurrectionDataParams): ResurrectionData {
  const { contentfulOn } = useResurrectionFlags()

  // Fetch Contentful data when enabled and slug is available
  const {
    result: { contentfulData },
    isLoading: isContentfulLoading
  } = useResurrectionCampaign(
    campaignSlug,
    actions,
    isResurrectionCampaignLoading
  )

  return useMemo(() => {
    // Determine which data source to use
    const useContentful = contentfulOn && !!contentfulData

    // Determine loading state - if Contentful is enabled but we're falling back to legacy,
    // we should use the legacy loading state
    const isLoading = contentfulOn
      ? isContentfulLoading && !contentfulData
      : isResurrectionCampaignLoading

    if (useContentful) {
      // Use Contentful data (GraphQL-based)
      return {
        heroTitle: contentfulData.heroTitle || '',
        heroSubtitle: contentfulData.heroSubtitle || '',
        heroPromoCopy:
          contentfulData.heroText || contentfulData.heroPromotionCopy || '',
        ctaLabel: contentfulData.ctaLabel || 'Restart My Orders',
        heroImage:
          contentfulData.heroImageUrl || contentfulData.heroImage || '',
        heroImageMobile: contentfulData.heroImageMobileUrl,
        browserTitle: contentfulData.browserTitle || '',
        featuredMeals: contentfulData.featuredMeals || [],
        campaignId: contentfulData.id || campaignSlug,
        couponCode: contentfulData.couponCode,
        conditionsCopy: contentfulData.conditionsCopy || '',
        highlightedMealsIds: contentfulData.highlightedMealsIds || [],
        chefBrandIds: contentfulData.chefBrandIds || [],
        isLoading: false,
        isError: false,
        newDesign: contentfulData.newDesign
      }
    }

    // Use router props (Redux-based)
    return {
      heroTitle: heroTitle || resurrectionCampaign?.heroTitle || '',
      heroSubtitle: heroSubtitle || resurrectionCampaign?.heroSubtitle || '',
      heroPromoCopy: text || resurrectionCampaign?.heroPromotionCopy || '',
      ctaLabel: 'Restart My Orders',
      heroImage: resurrectionCampaign?.heroImageUrl || '',
      heroImageMobile: undefined,
      browserTitle: browserTitle || '',
      featuredMeals: featuredMeals?.data || [],
      campaignId: resurrectionCampaign?.id || campaignSlug,
      couponCode: coupon ?? resurrectionCampaign?.couponCode,
      conditionsCopy:
        conditionsText || resurrectionCampaign?.conditionsCopy || '',
      highlightedMealsIds: resurrectionCampaign?.highlightedMealsIds || [],
      chefBrandIds: [],
      isLoading,
      isError: false
    }
  }, [
    contentfulOn,
    contentfulData,
    isResurrectionCampaignLoading,
    isContentfulLoading,
    resurrectionCampaign,
    featuredMeals,
    heroTitle,
    heroSubtitle,
    text,
    conditionsText,
    browserTitle,
    coupon,
    campaignSlug
  ])
}
